import React, { useState, useEffect, useRef, useCallback } from 'react';
import { IconButton, Tooltip, CircularProgress, Box } from '@mui/material';
import { Mic as MicIcon, MicOff as MicOffIcon, Pause as PauseIcon, PlayArrow as PlayArrowIcon } from '@mui/icons-material';
import { speechApi } from '../services/api';

interface VoiceInputButtonProps {
  onTranscription: (text: string) => void;
  disabled?: boolean;
  size?: 'small' | 'medium' | 'large';
  color?: 'primary' | 'secondary' | 'default' | 'error' | 'info' | 'success' | 'warning';
}

type SpeechMode = 'whisper' | 'webSpeech' | null;
type RecordingState = 'idle' | 'recording' | 'paused';

const VoiceInputButton: React.FC<VoiceInputButtonProps> = ({
  onTranscription,
  disabled = false,
  size = 'medium',
  color = 'primary',
}) => {
  const [recordingState, setRecordingState] = useState<RecordingState>('idle');
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [speechSupported, setSpeechSupported] = useState(false);
  const [speechMode, setSpeechMode] = useState<SpeechMode>(null);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const webSpeechRecognitionRef = useRef<any>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const isStoppingRef = useRef(false);

  // Memoize onTranscription to prevent unnecessary re-renders
  const onTranscriptionRef = useRef(onTranscription);
  onTranscriptionRef.current = onTranscription;

  // Check for speech support on mount
  useEffect(() => {
    const checkSpeechSupport = async () => {
      // Check MediaRecorder support (for Whisper)
      const browserSupport = !!(
        navigator.mediaDevices &&
        typeof navigator.mediaDevices.getUserMedia === 'function' &&
        typeof window.MediaRecorder === 'function'
      );

      // Check Web Speech API support (Fallback)
      const webSpeechSupport = !!(
        (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition
      );

      // Check if Whisper service is available
      try {
        const response = await speechApi.health();
        if (response.data.status === 'healthy' && browserSupport) {
          console.log('VoiceInput: Whisper Service verfügbar');
          setSpeechMode('whisper');
          setSpeechSupported(true);
          return;
        }
      } catch (error) {
        console.log('VoiceInput: Whisper Service nicht erreichbar');
      }

      // Fallback to Web Speech API
      if (webSpeechSupport) {
        console.log('VoiceInput: Web Speech API als Fallback');
        setSpeechMode('webSpeech');
        setSpeechSupported(true);
        return;
      }

      console.log('VoiceInput: Keine Spracherkennung verfügbar');
      setSpeechSupported(false);
    };

    checkSpeechSupport();
  }, []);

  // Setup MediaRecorder for Whisper mode
  useEffect(() => {
    if (speechMode !== 'whisper') return;

    let isMounted = true;

    const setupMediaRecorder = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        if (!isMounted) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }
        streamRef.current = stream;

        const mediaRecorder = new MediaRecorder(stream, {
          mimeType: MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : 'audio/mp4',
        });

        mediaRecorder.ondataavailable = (event) => {
          if (event.data.size > 0) {
            audioChunksRef.current.push(event.data);
          }
        };

        mediaRecorder.onstop = async () => {
          // Only process if we intentionally stopped (not due to error)
          if (!isStoppingRef.current) {
            // Unexpected stop - reset state
            setRecordingState('idle');
            audioChunksRef.current = [];
            return;
          }
          isStoppingRef.current = false;

          const audioBlob = new Blob(audioChunksRef.current, { type: mediaRecorder.mimeType });
          audioChunksRef.current = [];

          if (audioBlob.size > 0) {
            setIsTranscribing(true);
            try {
              const response = await speechApi.transcribe(audioBlob, 'de');
              if (response.data.text && isMounted) {
                onTranscriptionRef.current(response.data.text);
              }
            } catch (error) {
              console.error('Fehler bei der Transkription:', error);
            } finally {
              if (isMounted) {
                setIsTranscribing(false);
              }
            }
          }
        };

        // Sync state with actual MediaRecorder state
        mediaRecorder.onpause = () => {
          if (isMounted) setRecordingState('paused');
        };

        mediaRecorder.onresume = () => {
          if (isMounted) setRecordingState('recording');
        };

        mediaRecorder.onstart = () => {
          if (isMounted) setRecordingState('recording');
        };

        mediaRecorder.onerror = (event) => {
          console.error('MediaRecorder Fehler:', event);
          if (isMounted) {
            setRecordingState('idle');
            audioChunksRef.current = [];
          }
        };

        mediaRecorderRef.current = mediaRecorder;
      } catch (error) {
        console.error('Fehler beim Zugriff auf das Mikrofon:', error);
        if (isMounted) {
          setSpeechSupported(false);
        }
      }
    };

    setupMediaRecorder();

    return () => {
      isMounted = false;
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }
    };
  }, [speechMode]);

  // Setup Web Speech API
  useEffect(() => {
    if (speechMode !== 'webSpeech') return;

    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SpeechRecognition) return;

    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = false;
    recognition.lang = 'de-DE';

    recognition.onresult = (event: any) => {
      let transcript = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        if (event.results[i].isFinal) {
          transcript += event.results[i][0].transcript;
        }
      }
      if (transcript) {
        onTranscriptionRef.current(transcript);
      }
    };

    recognition.onerror = (event: any) => {
      console.error('Web Speech API Fehler:', event.error);
      setRecordingState('idle');
    };

    recognition.onend = () => {
      // Only set to idle if we're not paused
      setRecordingState((prev) => prev === 'paused' ? 'paused' : 'idle');
    };

    webSpeechRecognitionRef.current = recognition;
  }, [speechMode]);

  const handleStartRecording = useCallback(() => {
    if (speechMode === 'whisper' && mediaRecorderRef.current) {
      if (mediaRecorderRef.current.state === 'inactive') {
        audioChunksRef.current = [];
        mediaRecorderRef.current.start();
        // State wird durch onstart Event gesetzt
      }
    } else if (speechMode === 'webSpeech' && webSpeechRecognitionRef.current) {
      try {
        webSpeechRecognitionRef.current.start();
        setRecordingState('recording');
      } catch (error) {
        console.error('Web Speech API Start-Fehler:', error);
      }
    }
  }, [speechMode]);

  const handlePauseRecording = useCallback(() => {
    if (speechMode === 'whisper' && mediaRecorderRef.current) {
      if (mediaRecorderRef.current.state === 'recording') {
        mediaRecorderRef.current.pause();
        // State wird durch onpause Event gesetzt
      }
    } else if (speechMode === 'webSpeech' && webSpeechRecognitionRef.current) {
      // Web Speech API doesn't support pause, so we stop and remember we're paused
      webSpeechRecognitionRef.current.stop();
      setRecordingState('paused');
    }
  }, [speechMode]);

  const handleResumeRecording = useCallback(() => {
    if (speechMode === 'whisper' && mediaRecorderRef.current) {
      if (mediaRecorderRef.current.state === 'paused') {
        mediaRecorderRef.current.resume();
        // State wird durch onresume Event gesetzt
      }
    } else if (speechMode === 'webSpeech' && webSpeechRecognitionRef.current) {
      // Web Speech API - restart recognition
      try {
        webSpeechRecognitionRef.current.start();
        setRecordingState('recording');
      } catch (error) {
        console.error('Web Speech API Resume-Fehler:', error);
      }
    }
  }, [speechMode]);

  const handleStopRecording = useCallback(() => {
    if (speechMode === 'whisper' && mediaRecorderRef.current) {
      const state = mediaRecorderRef.current.state;
      if (state === 'recording' || state === 'paused') {
        isStoppingRef.current = true;
        mediaRecorderRef.current.stop();
      }
    } else if (speechMode === 'webSpeech' && webSpeechRecognitionRef.current) {
      webSpeechRecognitionRef.current.stop();
    }
    setRecordingState('idle');
  }, [speechMode]);

  const handleMicClick = useCallback(() => {
    if (recordingState === 'idle') {
      handleStartRecording();
    } else {
      handleStopRecording();
    }
  }, [recordingState, handleStartRecording, handleStopRecording]);

  const handlePauseClick = useCallback(() => {
    if (recordingState === 'recording') {
      handlePauseRecording();
    } else if (recordingState === 'paused') {
      handleResumeRecording();
    }
  }, [recordingState, handlePauseRecording, handleResumeRecording]);

  if (!speechSupported) {
    return null;
  }

  const getMicTooltip = () => {
    if (isTranscribing) return 'Transkribiere...';
    if (recordingState !== 'idle') return 'Aufnahme beenden';
    return `Spracheingabe starten (${speechMode === 'whisper' ? 'Whisper' : 'Browser'})`;
  };

  const getPauseTooltip = () => {
    if (recordingState === 'paused') return 'Aufnahme fortsetzen';
    return 'Aufnahme pausieren';
  };

  const isRecordingOrPaused = recordingState !== 'idle';

  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
      {/* Mic Button - Start/Stop */}
      <Tooltip title={getMicTooltip()}>
        <span>
          <IconButton
            onClick={handleMicClick}
            disabled={disabled || isTranscribing}
            color={isRecordingOrPaused ? 'error' : color}
            size={size}
          >
            {isTranscribing ? (
              <CircularProgress size={size === 'small' ? 16 : size === 'large' ? 28 : 24} />
            ) : isRecordingOrPaused ? (
              <MicOffIcon />
            ) : (
              <MicIcon />
            )}
          </IconButton>
        </span>
      </Tooltip>

      {/* Pause/Resume Button - only visible when recording or paused */}
      {isRecordingOrPaused && !isTranscribing && (
        <Tooltip title={getPauseTooltip()}>
          <span>
            <IconButton
              onClick={handlePauseClick}
              disabled={disabled}
              color={recordingState === 'paused' ? 'warning' : 'default'}
              size={size}
            >
              {recordingState === 'paused' ? (
                <PlayArrowIcon />
              ) : (
                <PauseIcon />
              )}
            </IconButton>
          </span>
        </Tooltip>
      )}
    </Box>
  );
};

export default VoiceInputButton;
